// frontend/src/pages/MarketingList.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import ReactPaginate from "react-paginate";
import Select from "react-select";
import { toast } from "react-toastify";
import {
    Loader2,
    ListFilter,
    RefreshCw,
    Save,
    Users,
    UserPlus,
    Mail,
    Linkedin,
    ChevronDown,
} from "lucide-react";

const PAGE_SIZE = 30;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const logicOptions = [
    { value: "AND", label: "AND (all conditions)" },
    { value: "OR", label: "OR (any condition)" },
];

const defaultContactFilters = {
    ai_positions: [],
    statuses: [],
    follower_min: 0,
    follower_max: 10000,
    require_business_email: false, // client-side only
    require_profile_url: false, // client-side only
    email_statuses: [],
};

const defaultRecordFilters = {
    categories: [],
    countries: [],
    keywords: [],
    relevance_min: 50,
    relevance_max: 100,
    authority_min: 0,
    authority_max: 100,
};

const emailStatusOptions = [
    { value: "verified", label: "Verified" },
    { value: "unverified", label: "Unverified" },
    { value: "expired", label: "Expired" },
    { value: "na", label: "N/A" },
];

const normEmailStatus = (s) => {
    const v = String(s || "").trim().toLowerCase();
    if (!v || v === "n/a" || v === "(n/a)" || v === "na") return "na";
    if (v === "verified") return "verified";
    if (v === "unverified") return "unverified";
    if (v === "expired") return "expired";
    return v;
};

const toOpt = (v) => ({ value: v, label: v });

const fmtDate = (s) => {
    if (!s) return "";
    const d = new Date(String(s).replace(/ GMT$/, ""));
    if (Number.isNaN(d.getTime())) return String(s);
    const dd = String(d.getDate()).padStart(2, "0");
    const mon = d.toLocaleString("en-AU", { month: "short" });
    const yr = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${dd} ${mon}, ${yr} ${hh}:${mm}`;
};

const scheduleFreqOptions = [
    { value: "daily", label: "Once per day" },
    { value: "weekly", label: "Once per week" },
    { value: "monthly", label: "Once per month" },
];

export default function MarketingList() {
    // options
    const [aiOptions, setAiOptions] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [countryOptions, setCountryOptions] = useState([]);
    const [keywordOptions, setKeywordOptions] = useState([]);

    // saved lists
    const [lists, setLists] = useState([]);
    const [selectedList, setSelectedList] = useState(null);

    // filters
    const [logic, setLogic] = useState(logicOptions[0]);
    const [contactF, setContactF] = useState({ ...defaultContactFilters });
    const [recordF, setRecordF] = useState({ ...defaultRecordFilters });

    // paging/sorting
    const [page, setPage] = useState(0);
    const [sortField, setSortField] = useState("created_at");
    const [sortOrder, setSortOrder] = useState("desc");

    // data
    const [rows, setRows] = useState([]); // holds ALL rows
    const [loading, setLoading] = useState(false);

    const [expandiBusy, setExpandiBusy] = useState(false);

    // save draft modal
    const [saveDraftOpen, setSaveDraftOpen] = useState(false);
    const [saveDraft, setSaveDraft] = useState({ name: "", description: "" });

    // connect on linkedin modal - once-off / recurring schedule
    const [connectModalOpen, setConnectModalOpen] = useState(false);
    const [connectMode, setConnectMode] = useState("once"); // "once" | "schedule"
    const [connectFreq, setConnectFreq] = useState(scheduleFreqOptions[0]);
    const [connectSkipSent, setConnectSkipSent] = useState(true);
    const [connectSchedulingBusy, setConnectSchedulingBusy] = useState(false);
    const [linkedinSchedulerInfo, setLinkedinSchedulerInfo] = useState(null);

    // email queue modal state
    const [emailMode, setEmailMode] = useState("once"); // "once" | "schedule"
    const [emailFreq, setEmailFreq] = useState(scheduleFreqOptions[0]);
    const [emailSkipSent, setEmailSkipSent] = useState(true);
    const [emailSchedulingBusy, setEmailSchedulingBusy] = useState(false);
    const [emailSchedulerInfo, setEmailSchedulerInfo] = useState(null);

    // add to email queue modal - once-off / recurring schedule
    const [emailQueueModalOpen, setEmailQueueModalOpen] = useState(false);

    // --- shared UI tokens (match other pages) ---
    const cardClass = "rounded-2xl border border-slate-200 bg-white shadow-sm";
    const labelClass =
        "mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500";
    const inputClass =
        "h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-700 shadow-sm outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-900/20";
    const numberClass =
        "h-11 w-24 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-700 shadow-sm outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-900/20";
    const btnPrimary =
        "inline-flex h-11 items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900/30 disabled:cursor-not-allowed disabled:opacity-50";
    const btnSecondary =
        "inline-flex h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-900/20 disabled:cursor-not-allowed disabled:opacity-50";
    const btnSoft =
        "inline-flex h-11 items-center justify-center gap-2 rounded-2xl bg-slate-100 px-4 text-sm font-semibold text-slate-800 transition hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900/20 disabled:cursor-not-allowed disabled:opacity-50";
    const iconBtn =
        "inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-900/20 disabled:cursor-not-allowed disabled:opacity-50";

    const pill = "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset";
    const pillSlate = `${pill} bg-slate-50 text-slate-700 ring-slate-200`;
    const pillEmerald = `${pill} bg-emerald-50 text-emerald-700 ring-emerald-200`;
    const pillAmber = `${pill} bg-amber-50 text-amber-700 ring-amber-200`;
    const pillRose = `${pill} bg-rose-50 text-rose-700 ring-rose-200`;

    const selectStyles44 = useMemo(
        () => ({
            control: (base, state) => ({
                ...base,
                minHeight: 44,
                borderRadius: 16,
                borderColor: state.isFocused ? "#0f172a" : "#cbd5e1",
                boxShadow: "none",
                backgroundColor: "white",
                ":hover": { borderColor: "#0f172a" },
            }),
            valueContainer: (base) => ({ ...base, paddingLeft: 12, paddingRight: 12 }),
            indicatorsContainer: (base) => ({ ...base, paddingRight: 6 }),
            menu: (base) => ({ ...base, borderRadius: 16, overflow: "hidden" }),
            option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected ? "#0f172a" : state.isFocused ? "#f1f5f9" : "white",
                color: state.isSelected ? "white" : "#0f172a",
            }),
            multiValue: (base) => ({ ...base, borderRadius: 9999 }),
            multiValueLabel: (base) => ({ ...base, fontWeight: 600 }),
            singleValue: (base) => ({ ...base, color: "#0f172a" }),
            placeholder: (base) => ({ ...base, color: "#64748b" }),
        }),
        []
    );

    // ----- effects -----
    useEffect(() => {
        (async () => {
            try {
                const { data } = await axiosInstance.get("/marketing-lists/options");
                const ai = [...new Set([...(data.ai_positions || []), "N/A"])];
                setAiOptions(ai.map(toOpt));
                setStatusOptions(["", ...(data.contact_status || [])].map((v) => toOpt(v || "All")));
                setCategoryOptions((data.business_category || []).map(toOpt));
                setCountryOptions((data.countries || []).map(toOpt));
                setKeywordOptions((data.controlled_keywords || []).map(toOpt));
            } catch {
                toast.error("Failed to load filter options");
            }
        })();
        loadLists();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setPage(0);
    }, [contactF.require_business_email, contactF.require_profile_url, contactF.email_statuses]);

    const buildPayload = () => {
        const ai_positions = (contactF.ai_positions || []).map((o) => o.value);
        const statuses = (contactF.statuses || [])
            .filter((o) => o.value && o.value.toLowerCase() !== "all")
            .map((o) => o.value);

        const categories = (recordF.categories || []).map((o) => o.value);
        const countries = (recordF.countries || []).map((o) => o.value);
        const keywords = (recordF.keywords || []).map((o) => o.value);

        return {
            logic: logic.value,
            filters: {
                ai_positions,
                statuses,
                follower_min: +contactF.follower_min,
                follower_max: +contactF.follower_max,
                categories,
                countries,
                keywords,
                relevance_min: +recordF.relevance_min,
                relevance_max: +recordF.relevance_max,
                authority_min: +recordF.authority_min,
                authority_max: +recordF.authority_max,
            },
        };
    };

    const submitQuery = async (payload) => {
        const { data, status } = await axiosInstance.post("/marketing-lists/query", payload, {
            timeout: 15000,
        });
        if (status !== 202 || !data?.job_id) throw new Error("Submit failed");
        return data.job_id;
    };

    const pollJob = async (jobId, maxMs = 15 * 60 * 1000) => {
        let delay = 1500;
        const deadline = Date.now() + maxMs;
        // eslint-disable-next-line no-constant-condition
        for (; ;) {
            const res = await axiosInstance.get(`/marketing-lists/jobs/${jobId}`, {
                timeout: 30000,
                headers: { "Cache-Control": "no-store" },
            });
            const st = res.data?.status;
            if (st === "done") return res.data.result;
            if (st === "failed") throw new Error("Server job failed");
            if (st === "expired") throw new Error("Result expired");
            if (Date.now() > deadline) throw new Error("Client timed out");
            await sleep(delay);
            delay = Math.min(delay * 1.6, 10000);
        }
    };

    const loadLists = async () => {
        try {
            const { data } = await axiosInstance.get("/marketing-lists");
            setLists(data || []);
        } catch {
            toast.error("Failed to load saved lists");
        }
    };

    const onSelectSaved = async (opt) => {
        if (!opt) {
            setSelectedList(null);
            return;
        }
        const ml = lists.find((x) => x.marketing_list_id === opt.value);
        if (!ml) return;

        setSelectedList(ml);

        const f = ml.filters || {};
        setContactF({
            ai_positions: (f.ai_positions || []).map(toOpt),
            statuses: (f.statuses || []).length ? (f.statuses || []).map(toOpt) : [],
            follower_min: f.follower_min ?? 0,
            follower_max: f.follower_max ?? 100000,
            require_business_email: false,
            require_profile_url: false,
            email_statuses: [],
        });
        setRecordF({
            categories: (f.categories || []).map(toOpt),
            countries: (f.countries || []).map(toOpt),
            keywords: (f.keywords || []).map(toOpt),
            relevance_min: f.relevance_min ?? defaultRecordFilters.relevance_min,
            relevance_max: f.relevance_max ?? defaultRecordFilters.relevance_max,
            authority_min: f.authority_min ?? defaultRecordFilters.authority_min,
            authority_max: f.authority_max ?? defaultRecordFilters.authority_max,
        });
        setLogic(logicOptions.find((l) => l.value === (ml.logic || "AND")) || logicOptions[0]);
        setPage(0);

        const payloadFromSaved = {
            logic: ml.logic || "AND",
            filters: {
                ai_positions: f.ai_positions || [],
                statuses: f.statuses || [],
                follower_min: Number(f.follower_min ?? 0),
                follower_max: Number(f.follower_max ?? 100000),
                categories: f.categories || [],
                countries: f.countries || [],
                keywords: f.keywords || [],
                relevance_min: Number(f.relevance_min ?? defaultRecordFilters.relevance_min),
                relevance_max: Number(f.relevance_max ?? defaultRecordFilters.relevance_max),
                authority_min: Number(f.authority_min ?? defaultRecordFilters.authority_min),
                authority_max: Number(f.authority_max ?? defaultRecordFilters.authority_max),
            },
        };

        await handleLoad(payloadFromSaved);
    };

    const aliveRef = useRef(true);
    useEffect(() => {
        aliveRef.current = true;
        return () => {
            aliveRef.current = false;
        };
    }, []);

    const handleLoad = async (overridePayload = null) => {
        toast.info("Processing in background");
        setRows([]);
        setPage(0);
        setLoading(true);
        try {
            const payload = overridePayload ?? buildPayload();
            const jobId = await submitQuery(payload);
            const result = await pollJob(jobId);
            if (!aliveRef.current) return;
            setRows(result?.rows || []);
        } catch (e) {
            toast.error(e?.message || "Query failed");
        } finally {
            setLoading(false);
        }
    };

    const filteredRows = useMemo(() => {
        const hasVal = (v) => String(v ?? "").trim().length > 0;
        const chosen = (contactF.email_statuses || []).map((o) => o.value);
        return rows.filter((r) => {
            const okEmail = !contactF.require_business_email || hasVal(r.business_email);
            const okProfile = !contactF.require_profile_url || hasVal(r.profile_url);
            const stNorm = normEmailStatus(r.business_email_status);
            const okStatus = chosen.length === 0 || chosen.includes(stNorm);
            return okEmail && okProfile && okStatus;
        });
    }, [rows, contactF.require_business_email, contactF.require_profile_url, contactF.email_statuses]);

    const effectiveTotal = filteredRows.length;
    const pageCount = Math.ceil(effectiveTotal / PAGE_SIZE) || 1;

    const sortedRows = useMemo(() => {
        const arr = [...filteredRows];
        const key = (r) => {
            switch (sortField) {
                case "relevance":
                case "authority":
                    return Number(r[sortField] ?? 0);
                case "created_at":
                    return new Date(r.created_at || 0).getTime();
                default:
                    return (r[sortField] || "").toString().toLowerCase();
            }
        };
        arr.sort((a, b) => {
            const ka = key(a);
            const kb = key(b);
            if (ka < kb) return sortOrder === "asc" ? -1 : 1;
            if (ka > kb) return sortOrder === "asc" ? 1 : -1;
            return 0;
        });
        return arr;
    }, [filteredRows, sortField, sortOrder]);

    const pagedRows = useMemo(() => {
        const start = page * PAGE_SIZE;
        const end = start + PAGE_SIZE;
        return sortedRows.slice(start, end);
    }, [sortedRows, page]);

    const sortClick = (field) => {
        if (sortField !== field) {
            setSortField(field);
            setSortOrder("asc");
        } else if (sortOrder === "asc") {
            setSortOrder("desc");
        } else {
            setSortField("created_at");
            setSortOrder("desc");
        }
    };

    const sortArrow = (field) => (sortField === field ? (sortOrder === "asc" ? " ↑" : " ↓") : "");

    const savedListOptions = useMemo(
        () =>
            (lists || []).map((l) => ({
                value: l.marketing_list_id,
                label: l.name,
                description: l.description || "",
                created_at: l.created_at || l.createdAt || null,
            })),
        [lists]
    );

    const savedListStyles = useMemo(
        () => ({
            ...selectStyles44,
            option: (base) => ({
                ...base,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
            }),
        }),
        [selectStyles44]
    );

    const fmtListDate = (s) => {
        if (!s) return "";
        const d = new Date(String(s));
        if (Number.isNaN(d.getTime())) return "";
        const dd = String(d.getDate()).padStart(2, "0");
        const mon = d.toLocaleString("en-AU", { month: "short" });
        const yy = String(d.getFullYear()).slice(-2);
        return `${dd}-${mon}-${yy}`;
    };

    const renderSavedLabel = ({ label, description, created_at }) => {
        const date = fmtListDate(created_at);
        const parts = [label.trim()];
        if (description) parts.push(`— ${String(description).trim()}`);
        if (date) parts.push(`(${date})`);
        return parts.join(" ");
    };

    const linkifyDomain = (d, displayText) => {
        if (!d) return "N/A";
        const href = d.startsWith("http") ? d : `https://${d}`;
        const text = displayText ?? d;
        return (
            <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="block max-w-[180px] truncate align-middle font-medium text-blue-600 hover:underline"
                title={href}
                onClick={(e) => e.stopPropagation()}
            >
                {text}
            </a>
        );
    };

    const linkifyEmail = (eaddr) => {
        if (!eaddr) return "N/A";
        return (
            <a
                href={`mailto:${eaddr}`}
                className="font-medium text-blue-600 hover:underline"
                onClick={(e) => e.stopPropagation()}
            >
                {eaddr}
            </a>
        );
    };

    const linkedinSuffix = (url) => {
        if (!url) return "";
        try {
            const { pathname } = new URL(url);
            const m = pathname.match(/^\/in\/([^/]+)/i);
            return m ? decodeURIComponent(m[1]) : url;
        } catch {
            const m = url.match(/linkedin\.com\/in\/([^/?#]+)/i);
            return m ? decodeURIComponent(m[1]) : url;
        }
    };

    const handleCreateCrmLeads = async () => {
        const items = filteredRows
            .map((r) => ({ contact_id: r.contact_id, record_id: r.record_id }))
            .filter((x) => x.contact_id && x.record_id);

        if (!items.length) {
            toast.warn("No valid rows with contact_id and record_id");
            return;
        }

        try {
            await axiosInstance.post("/marketing-lists/create_leads_pairs_async", { pairs: items }, { timeout: 60000 });
            toast.success("Lead creation started in background.");
        } catch (err) {
            const msg = err?.response?.data?.message || "Create CRM leads failed";
            toast.error(msg);
        }
    };

    const handleExportToExpandi = async () => {
        const profile_urls = Array.from(
            new Set(
                filteredRows
                    .map((r) => String(r.profile_url || "").trim())
                    .filter(Boolean)
            )
        );

        if (!profile_urls.length) {
            toast.warn("No LinkedIn URLs to export from current filter");
            return;
        }

        try {
            setExpandiBusy(true);
            await axiosInstance.post("/webhook/expandi-campaign-assign", { profile_links: profile_urls }, { timeout: 60000 });
            toast.success(`Exported ${profile_urls.length} URL(s) to Expandi`);
        } catch (err) {
            const msg = err?.response?.data?.message || "Expandi export failed";
            toast.error(msg);
        } finally {
            setExpandiBusy(false);
        }
    };

    const handleSendToMailchimp = () => {
        const n = filteredRows.length;
        toast.info(`Send to Mailchimp triggered for ${n} records (placeholder)`);
    };

    const handleAddtoEmailQueue = async () => {
        const items = filteredRows
            .map((r) => ({ contact_id: r.contact_id, record_id: r.record_id }))
            .filter((x) => x.contact_id && x.record_id);

        if (!items.length) {
            toast.warn("No valid rows with contact_id and record_id");
            return;
        }

        try {
            const res = await axiosInstance.post("/marketing-lists/add_to_email_queue", { pairs: items }, { timeout: 60000 });

            const msg = res?.data?.message || "Done";
            const t = res?.data?.totals || {};
            if (res.status === 202) {
                toast.success(
                    `${msg} Started ${t.enqueued}/${t.requested}. Pending: ${t.already_pending || 0}. No email: ${t.no_email || 0}.`
                );
            } else {
                toast.info(`${msg} Pending: ${t.already_pending || 0}. No email: ${t.no_email || 0}.`);
            }
        } catch (err) {
            const msg = err?.response?.data?.error || err?.response?.data?.message || "Add to email queue failed";
            toast.error(msg);
        }
    };

    // --- single-row handlers ---
    const handleSingleCreateCrmLeads = async (row) => {
        const contact_id = Number(row?.contact_id);
        const record_id = Number(row?.record_id);
        if (!contact_id || !record_id) {
            toast.warn("Row missing contact_id or record_id");
            return;
        }
        try {
            await axiosInstance.post(
                "/marketing-lists/create_leads_pairs_async",
                { pairs: [{ contact_id, record_id }] },
                { timeout: 30000 }
            );
            toast.success("Lead creation started for this row.");
        } catch (err) {
            const msg = err?.response?.data?.message || "Single CRM lead create failed";
            toast.error(msg);
        }
    };

    const handleSingleExportToExpandi = async (row) => {
        const url = String(row?.profile_url || "").trim();
        if (!url) {
            toast.warn("No LinkedIn URL on this row");
            return;
        }
        try {
            await axiosInstance.post("/webhook/expandi-campaign-assign", { profile_links: [url] }, { timeout: 30000 });
            toast.success("Exported 1 URL to Expandi");
        } catch (err) {
            const msg = err?.response?.data?.message || "Single Expandi export failed";
            toast.error(msg);
        }
    };

    const handleSingleSendToMailchimp = (row) => {
        toast.info(`Send to Mailchimp triggered for 1 record (placeholder)`);
    };

    const handleSingleAddtoEmailQueue = async (row) => {
        const contact_id = Number(row?.contact_id);
        const record_id = Number(row?.record_id);
        if (!contact_id || !record_id) {
            toast.warn("Row missing contact_id or record_id");
            return;
        }

        try {
            const res = await axiosInstance.post(
                "/marketing-lists/add_to_email_queue",
                { pairs: [{ contact_id, record_id }] },
                { timeout: 30000 }
            );

            const msg = res?.data?.message || "Done";
            const t = res?.data?.totals || {};

            if (res.status === 202 && (t.enqueued ?? 0) > 0) {
                toast.success("Email generation started.");
            } else if (res.status === 200 && (t.already_pending ?? 0) > 0) {
                toast.info("Email already pending in queue.");
            } else if (res.status === 200 && (t.no_email ?? 0) > 0) {
                toast.warn("Contact has no email.");
            } else {
                toast.info(msg);
            }
        } catch (err) {
            const msg = err?.response?.data?.error || err?.response?.data?.message || "Single add to email queue failed";
            toast.error(msg);
        }
    };

    const getUniqueProfileUrlsFromFilter = () =>
        Array.from(new Set(filteredRows.map((r) => String(r.profile_url || "").trim()).filter(Boolean)));

    const getPairsFromFilter = () =>
        filteredRows
            .map((r) => ({
                contact_id: Number(r.contact_id),
                record_id: Number(r.record_id),
            }))
            .filter((x) => x.contact_id && x.record_id);

    const handleScheduleLinkedInConnect = async () => {
        const profile_urls = getUniqueProfileUrlsFromFilter();
        if (!profile_urls.length) {
            toast.warn("No LinkedIn URLs from current filter");
            return;
        }

        try {
            setConnectSchedulingBusy(true);

            await axiosInstance.post(
                "/marketing-lists/schedulers/linkedin-connect",
                {
                    frequency: connectFreq.value,
                    current_filter_json: buildPayload(),
                    skip_already_sent: !!connectSkipSent,
                    marketing_list_id: selectedList?.marketing_list_id || null,
                },
                { timeout: 60000 }
            );

            toast.success("LinkedIn connect scheduler set.");
            setConnectModalOpen(false);
        } catch (err) {
            const msg = err?.response?.data?.message || "Failed to set LinkedIn scheduler";
            toast.error(msg);
        } finally {
            setConnectSchedulingBusy(false);
        }
    };

    const handleScheduleEmailQueue = async () => {
        const pairs = getPairsFromFilter();
        if (!pairs.length) {
            toast.warn("No valid rows with contact_id and record_id");
            return;
        }

        try {
            setEmailSchedulingBusy(true);

            await axiosInstance.post(
                "/marketing-lists/schedulers/email-queue",
                {
                    frequency: emailFreq.value,
                    current_filter_json: buildPayload(),
                    skip_already_sent: !!emailSkipSent,
                    marketing_list_id: selectedList?.marketing_list_id || null,
                },
                { timeout: 60000 }
            );

            toast.success("Email queue scheduler set.");
            setEmailQueueModalOpen(false);
        } catch (err) {
            const msg = err?.response?.data?.message || "Failed to set email scheduler";
            toast.error(msg);
        } finally {
            setEmailSchedulingBusy(false);
        }
    };

    const querySchedulerInfo = async (type) => {
        if (!selectedList) return;
        try {
            const { data } = await axiosInstance.post(
                "/marketing-lists/schedulers/info",
                {
                    scheduler_type: type,
                    current_filter_json: buildPayload(),
                    marketing_list_id: selectedList?.marketing_list_id || null,
                },
                { timeout: 30000 }
            );
            if (type === "linkedin_connect") {
                setLinkedinSchedulerInfo(data.scheduler || null);
            } else if (type === "email_queue") {
                setEmailSchedulerInfo(data.scheduler || null);
            }
        } catch {
            toast.error("Failed to load scheduler info");
        }
    };

    const handleDeleteScheduler = (schedulerId) => async () => {
        if (!schedulerId) return;
        try {
            await axiosInstance.delete(`/marketing-lists/schedulers/${schedulerId}`, { timeout: 30000 });
            toast.success("Scheduler deleted");
            await querySchedulerInfo("linkedin_connect");
            await querySchedulerInfo("email_queue");
        } catch {
            toast.error("Failed to delete scheduler");
        }
    };

    const emailStatusPill = (raw) => {
        const key = String(raw || "").trim().toLowerCase();
        if (key === "verified") return { label: "Verified", cls: pillEmerald };
        if (key === "unverified") return { label: "Unverified", cls: pillAmber };
        if (key === "expired") return { label: "Expired", cls: pillRose };
        return { label: "N/A", cls: pillSlate };
    };

    const columns = [
        ["Domain", "domain"],
        ["Company Name", "company_name"],
        ["Business Category", "business_category"],
        ["Country", "country"],
        ["Relevance", "relevance"],
        ["Person Name", "person_name"],
        ["Business Email", "business_email"],
        ["Generic Email", "email"],
        ["AI Position", "ai_position"],
        ["LinkedIn", "profile_url"],
        ["Created At", "created_at"],
        ["Actions", "__actions"],
    ];

    return (
        <div className="min-h-screen bg-slate-50 p-4">
            <div className="mx-auto max-w-full space-y-6">
                {/* Header / Saved list */}
                <div className={`${cardClass} px-6 py-5`}>
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0">
                            <div className="flex items-center gap-3">
                                <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-sm">
                                    <Users className="h-5 w-5" />
                                </div>
                                <div className="min-w-0">
                                    <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">
                                        Marketing List
                                    </h1>
                                    <p className="mt-1 text-sm text-slate-600">
                                        Query, refine, and run actions on filtered contacts.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-12 lg:items-end">
                        <div className="lg:col-span-9">
                            <label className={labelClass}>Saved list</label>
                            <Select
                                placeholder="Select a saved list..."
                                value={
                                    selectedList
                                        ? {
                                            value: selectedList.marketing_list_id,
                                            label: selectedList.name,
                                            description: selectedList.description,
                                            created_at: selectedList.created_at || selectedList.createdAt || null,
                                        }
                                        : null
                                }
                                options={savedListOptions}
                                styles={savedListStyles}
                                onChange={onSelectSaved}
                                getOptionLabel={renderSavedLabel}
                            />
                        </div>

                        <div className="lg:col-span-3">
                            <label className={labelClass}>Results</label>
                            <div className="flex h-11 items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4">
                                <span className="text-sm font-semibold text-slate-700">Found</span>
                                <span className="text-sm font-extrabold text-slate-900 tabular-nums">
                                    {effectiveTotal || 0}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                    {/* Contact */}
                    <div className={`${cardClass} p-6`}>
                        <div className="mb-5 flex items-start justify-between gap-3">
                            <div>
                                <div className="text-lg font-bold text-slate-900">Contact Filters</div>
                                <div className="mt-1 text-sm text-slate-600">Who you want to reach</div>
                            </div>
                            <span className={pillSlate}>Contacts</span>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className={labelClass}>AI position</label>
                                <Select
                                    isMulti
                                    value={contactF.ai_positions}
                                    onChange={(v) => setContactF((s) => ({ ...s, ai_positions: v || [] }))}
                                    options={aiOptions}
                                    classNamePrefix="react-select"
                                    placeholder="Select AI position..."
                                    styles={selectStyles44}
                                />
                            </div>

                            <div>
                                <label className={labelClass}>Contact status</label>
                                <Select
                                    isMulti
                                    value={contactF.statuses}
                                    onChange={(v) => setContactF((s) => ({ ...s, statuses: v || [] }))}
                                    options={statusOptions}
                                    classNamePrefix="react-select"
                                    placeholder="All"
                                    styles={selectStyles44}
                                />
                            </div>

                            <div>
                                <label className={labelClass}>Follower count</label>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="number"
                                        min={0}
                                        value={contactF.follower_min}
                                        onChange={(e) => setContactF((s) => ({ ...s, follower_min: e.target.value || 0 }))}
                                        className={numberClass}
                                    />
                                    <span className="text-sm font-semibold text-slate-500">to</span>
                                    <input
                                        type="number"
                                        min={0}
                                        value={contactF.follower_max}
                                        onChange={(e) =>
                                            setContactF((s) => ({ ...s, follower_max: e.target.value || 100000 }))
                                        }
                                        className={numberClass}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className={labelClass}>Logic</label>
                                <Select
                                    value={logic}
                                    onChange={(v) => setLogic(v)}
                                    options={logicOptions}
                                    classNamePrefix="react-select"
                                    styles={selectStyles44}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Company */}
                    <div className={`${cardClass} p-6`}>
                        <div className="mb-5 flex items-start justify-between gap-3">
                            <div>
                                <div className="text-lg font-bold text-slate-900">Company Filters</div>
                                <div className="mt-1 text-sm text-slate-600">Where they work</div>
                            </div>
                            <span className={pillSlate}>Companies</span>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className={labelClass}>Business category</label>
                                <Select
                                    isMulti
                                    value={recordF.categories}
                                    onChange={(v) => setRecordF((s) => ({ ...s, categories: v || [] }))}
                                    options={categoryOptions}
                                    classNamePrefix="react-select"
                                    placeholder="Select categories..."
                                    styles={selectStyles44}
                                />
                            </div>

                            <div>
                                <label className={labelClass}>Country</label>
                                <Select
                                    isMulti
                                    value={recordF.countries}
                                    onChange={(v) => setRecordF((s) => ({ ...s, countries: v || [] }))}
                                    options={countryOptions}
                                    classNamePrefix="react-select"
                                    placeholder="Select countries..."
                                    styles={selectStyles44}
                                />
                            </div>

                            <div>
                                <label className={labelClass}>Keywords</label>
                                <Select
                                    isMulti
                                    value={recordF.keywords}
                                    onChange={(v) => setRecordF((s) => ({ ...s, keywords: v || [] }))}
                                    options={keywordOptions}
                                    classNamePrefix="react-select"
                                    placeholder="Select controlled keywords..."
                                    styles={selectStyles44}
                                />
                            </div>

                            <div>
                                <label className={labelClass}>Relevance</label>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        value={recordF.relevance_min}
                                        onChange={(e) => setRecordF((s) => ({ ...s, relevance_min: e.target.value || 0 }))}
                                        className={numberClass}
                                    />
                                    <span className="text-sm font-semibold text-slate-500">to</span>
                                    <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        value={recordF.relevance_max}
                                        onChange={(e) => setRecordF((s) => ({ ...s, relevance_max: e.target.value || 100 }))}
                                        className={numberClass}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className={labelClass}>Authority</label>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        value={recordF.authority_min}
                                        onChange={(e) => setRecordF((s) => ({ ...s, authority_min: e.target.value || 0 }))}
                                        className={numberClass}
                                    />
                                    <span className="text-sm font-semibold text-slate-500">to</span>
                                    <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        value={recordF.authority_max}
                                        onChange={(e) => setRecordF((s) => ({ ...s, authority_max: e.target.value || 100 }))}
                                        className={numberClass}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className={`${cardClass} p-6`}>
                        <div className="mb-5 flex items-start justify-between gap-3">
                            <div>
                                <div className="text-lg font-bold text-slate-900">Actions</div>
                                <div className="mt-1 text-sm text-slate-600">Run after loading results</div>
                            </div>
                            <span className={pillSlate}>Run</span>
                        </div>

                        <div className="flex flex-col gap-3">
                            <button
                                onClick={() => {
                                    setPage(0);
                                    handleLoad();
                                }}
                                disabled={loading}
                                aria-busy={loading}
                                className={btnPrimary}
                            >
                                {loading ? (
                                    <span className="flex items-center gap-2">
                                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                                        Loading...
                                    </span>
                                ) : (
                                    <>
                                        <ListFilter className="h-4 w-4" />
                                        Load list
                                    </>
                                )}
                            </button>

                            <button onClick={() => setSaveDraftOpen(true)} className={btnSecondary}>
                                <Save className="h-4 w-4" />
                                Save filters
                            </button>

                            <button
                                onClick={() => {
                                    setContactF({ ...defaultContactFilters });
                                    setRecordF({ ...defaultRecordFilters });
                                    setLogic(logicOptions[0]);
                                    setSelectedList(null);
                                    setRows([]);
                                    setPage(0);
                                    toast.info("Filters reset");
                                }}
                                className={btnSoft}
                            >
                                <RefreshCw className="h-4 w-4" />
                                Reset
                            </button>

                            <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                <div className="flex items-center justify-between">
                                    <div className="text-sm font-semibold text-slate-900">Current results</div>
                                    <span className="text-sm font-extrabold tabular-nums text-slate-900">
                                        {effectiveTotal || 0}
                                    </span>
                                </div>
                                <div className="mt-1 text-xs text-slate-600">
                                    Sorting:{" "}
                                    <span className="font-semibold text-slate-800">{sortField}</span>{" "}
                                    <span className="font-semibold text-slate-800">{sortOrder}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Post-load toolbar */}
                {effectiveTotal ? (
                    <div className={`${cardClass} p-6`}>
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                                <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm hover:bg-slate-50">
                                    <input
                                        type="checkbox"
                                        checked={!!contactF.require_business_email}
                                        onChange={(e) =>
                                            setContactF((s) => ({ ...s, require_business_email: e.target.checked }))
                                        }
                                        className="h-4 w-4 accent-slate-900"
                                    />
                                    <span className="text-sm font-semibold text-slate-900">Business email</span>
                                </label>

                                <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm hover:bg-slate-50">
                                    <input
                                        type="checkbox"
                                        checked={!!contactF.require_profile_url}
                                        onChange={(e) => setContactF((s) => ({ ...s, require_profile_url: e.target.checked }))}
                                        className="h-4 w-4 accent-slate-900"
                                    />
                                    <span className="text-sm font-semibold text-slate-900">LinkedIn URL</span>
                                </label>

                                <div className="min-w-[240px]">
                                    <Select
                                        isMulti
                                        placeholder="Email status"
                                        classNamePrefix="react-select"
                                        options={emailStatusOptions}
                                        value={contactF.email_statuses}
                                        onChange={(v) => setContactF((s) => ({ ...s, email_statuses: v || [] }))}
                                        styles={selectStyles44}
                                    />
                                </div>
                            </div>

                            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
                                <button onClick={handleCreateCrmLeads} disabled={loading} className={btnSecondary}>
                                    <UserPlus className="h-4 w-4" />
                                    Create CRM Leads
                                </button>

                                <button
                                    onClick={() => {
                                        setConnectModalOpen(true);
                                        querySchedulerInfo("linkedin_connect");
                                    }}
                                    disabled={loading || expandiBusy}
                                    className={btnSecondary}
                                >
                                    {expandiBusy ? (
                                        <span className="flex items-center gap-2">
                                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                                            Adding...
                                        </span>
                                    ) : (
                                        <>
                                            <Linkedin className="h-4 w-4" />
                                            Connect on LinkedIn
                                        </>
                                    )}
                                </button>

                                <button
                                    onClick={() => {
                                        setEmailQueueModalOpen(true);
                                        querySchedulerInfo("email_queue");
                                    }}
                                    disabled={loading}
                                    className={btnSecondary}
                                >
                                    <Mail className="h-4 w-4" />
                                    Add to Email Queue
                                </button>
                            </div>
                        </div>

                        <p className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm font-semibold text-rose-700">
                            If a contact is owned by a CRM owner other than &quot;upload-crm&quot;, email generation for that contact
                            is skipped.
                        </p>
                    </div>
                ) : null}

                {/* Results */}
                {/* <div className="flex items-center justify-between">
                    <div className="text-sm text-slate-600">
                        {effectiveTotal ? `Found ${effectiveTotal} results` : "No results"}
                    </div>
                </div> */}

                {/* Desktop table */}
                <div className={`${cardClass} hidden overflow-hidden md:block`}>
                    <div className="overflow-x-auto">
                        <table className="min-w-full table-auto border-collapse">
                            <thead>
                                <tr className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-600">
                                    {columns.map(([label, field], idx) => (
                                        <th
                                            key={field}
                                            className={[
                                                "whitespace-nowrap border-b border-slate-200 px-3 py-3 text-left",
                                                field !== "__actions" ? "cursor-pointer hover:bg-slate-100 transition" : "",
                                                idx === 0 ? "rounded-tl-2xl" : "",
                                                idx === columns.length - 1 ? "rounded-tr-2xl" : "",
                                            ].join(" ")}
                                            onClick={() => (field !== "__actions" ? (setPage(0) || sortClick(field)) : null)}
                                        >
                                            {label}
                                            {field !== "__actions" ? sortArrow(field) : null}
                                        </th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody className="text-sm text-slate-900">
                                {pagedRows.length ? (
                                    pagedRows.map((r, i) => {
                                        const st = emailStatusPill(r.business_email_status);
                                        return (
                                            <tr key={`${r.domain}-${i}`} className="border-b border-slate-100 hover:bg-slate-50">
                                                <td className="px-3 py-2">
                                                    <span className="inline-block max-w-[130px] truncate align-middle" title={r.domain || "N/A"}>
                                                        {linkifyDomain(r.domain)}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2">
                                                    <span className="inline-block max-w-[130px] truncate align-middle" title={r.company_name || "N/A"}>
                                                        {r.company_name || "N/A"}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2">{r.business_category || "N/A"}</td>
                                                <td className="px-3 py-2">{r.country || "N/A"}</td>
                                                <td className="px-3 py-2">{r.relevance ?? ""}</td>
                                                <td className="px-3 py-2">
                                                    <span className="inline-block max-w-[150px] truncate align-middle" title={r.person_name || "N/A"}>
                                                        {r.person_name || "N/A"}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2 min-w-[280px]">
                                                    <div className="flex items-center gap-2">
                                                        <div className="min-w-0 flex-1 truncate">{linkifyEmail(r.business_email)}</div>
                                                        <span className={`${st.cls} shrink-0 whitespace-nowrap`} title={r.business_email_status || "N/A"}>
                                                            {st.label}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2">
                                                    <span className="inline-block max-w-[210px] truncate align-middle" title={r.email || "N/A"}>
                                                        {linkifyEmail(r.email)}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2">
                                                    <span>{r.ai_position || "N/A"}</span>
                                                </td>
                                                <td className="px-3 py-2">
                                                    <span className="inline-block max-w-[150px] truncate align-middle" title={r.profile_url || "N/A"}>
                                                        {r.profile_url ? linkifyDomain(r.profile_url, linkedinSuffix(r.profile_url)) : "N/A"}
                                                    </span>
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-2">{fmtDate(r.created_at)}</td>
                                                <td className="px-3 py-2">
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleSingleCreateCrmLeads(r);
                                                            }}
                                                            className={iconBtn}
                                                            title="Create CRM Lead"
                                                            disabled={loading}
                                                            aria-label="Create CRM Lead"
                                                        >
                                                            <UserPlus className="h-4 w-4" />
                                                        </button>

                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleSingleExportToExpandi(r);
                                                            }}
                                                            className={iconBtn}
                                                            title="Connect on LinkedIn"
                                                            disabled={loading}
                                                            aria-label="Connect on LinkedIn"
                                                        >
                                                            <Linkedin className="h-4 w-4" />
                                                        </button>

                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleSingleAddtoEmailQueue(r);
                                                            }}
                                                            className={iconBtn}
                                                            title="Add to Email Queue"
                                                            disabled={loading}
                                                            aria-label="Add to Email Queue"
                                                        >
                                                            <Mail className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td className="px-5 py-10 text-center text-slate-500" colSpan={12}>
                                            {loading ? (
                                                <div className="space-y-2">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                                                        <p className="font-semibold text-slate-700">Loading...</p>
                                                    </div>
                                                    <p className="text-sm text-slate-600">It may take several minutes, please be patient.</p>
                                                </div>
                                            ) : (
                                                "No data"
                                            )}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Mobile cards */}
                <div className="space-y-3 md:hidden">
                    {pagedRows.length ? (
                        pagedRows.map((r, i) => {
                            const st = emailStatusPill(r.business_email_status);
                            return (
                                <div key={`${r.domain}-${i}`} className={`${cardClass} p-4`}>
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="text-sm font-extrabold text-slate-900">
                                                {r.company_name || "N/A"}
                                            </div>
                                            <div className="mt-1">{linkifyDomain(r.domain)}</div>
                                            <div className="mt-2 flex flex-wrap gap-2">
                                                <span className={pillSlate}>{r.country || "N/A"}</span>
                                                <span className={pillSlate}>{r.business_category || "N/A"}</span>
                                                <span className={pillSlate}>Relevance: {r.relevance ?? "—"}</span>
                                            </div>
                                        </div>

                                        <div className="flex shrink-0 items-center gap-2">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSingleCreateCrmLeads(r);
                                                }}
                                                className={iconBtn}
                                                title="Create CRM Lead"
                                                disabled={loading}
                                                aria-label="Create CRM Lead"
                                            >
                                                <UserPlus className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSingleExportToExpandi(r);
                                                }}
                                                className={iconBtn}
                                                title="Connect on LinkedIn"
                                                disabled={loading}
                                                aria-label="Connect on LinkedIn"
                                            >
                                                <Linkedin className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSingleAddtoEmailQueue(r);
                                                }}
                                                className={iconBtn}
                                                title="Add to Email Queue"
                                                disabled={loading}
                                                aria-label="Add to Email Queue"
                                            >
                                                <Mail className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="mt-4 grid grid-cols-1 gap-3">
                                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Person</div>
                                            <div className="mt-1 text-sm font-semibold text-slate-900">{r.person_name || "N/A"}</div>
                                            <div className="mt-2 text-xs font-semibold uppercase tracking-wide text-slate-500">AI position</div>
                                            <div className="mt-1">
                                                <span className={pillSlate}>{r.ai_position || "N/A"}</span>
                                            </div>
                                        </div>

                                        <div className="rounded-2xl border border-slate-200 bg-white p-3">
                                            <div className="flex items-center justify-between gap-2">
                                                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Business email</div>
                                                <span className={st.cls}>{st.label}</span>
                                            </div>
                                            <div className="mt-1">{linkifyEmail(r.business_email)}</div>

                                            <div className="mt-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Generic email</div>
                                            <div className="mt-1">{linkifyEmail(r.email)}</div>

                                            <div className="mt-3 text-xs font-semibold uppercase tracking-wide text-slate-500">LinkedIn</div>
                                            <div className="mt-1">
                                                {r.profile_url ? linkifyDomain(r.profile_url, linkedinSuffix(r.profile_url)) : "N/A"}
                                            </div>

                                            <div className="mt-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Created</div>
                                            <div className="mt-1 text-sm text-slate-700">{fmtDate(r.created_at)}</div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className={`${cardClass} p-6 text-center text-slate-600`}>
                            {loading ? (
                                <div className="space-y-2">
                                    <div className="flex items-center justify-center gap-2">
                                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                                        <p className="font-semibold text-slate-700">Loading...</p>
                                    </div>
                                    <p className="text-sm text-slate-600">It may take several minutes, please be patient.</p>
                                </div>
                            ) : (
                                "No data"
                            )}
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {pageCount > 1 && (
                    <div className={`${cardClass} px-4 py-3`}>
                        <ReactPaginate
                            previousLabel={
                                <span className="inline-flex h-10 items-center rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50">
                                    ← Previous
                                </span>
                            }
                            nextLabel={
                                <span className="inline-flex h-10 items-center rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50">
                                    Next →
                                </span>
                            }
                            pageCount={pageCount}
                            onPageChange={({ selected }) => setPage(selected)}
                            containerClassName="flex flex-wrap items-center justify-center gap-2"
                            pageClassName="block"
                            pageLinkClassName="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                            activeLinkClassName="!border-slate-900 !bg-slate-900 !text-white"
                            disabledClassName="cursor-not-allowed opacity-50"
                            breakClassName="px-2 text-slate-400"
                            renderOnZeroPageCount={null}
                        />
                    </div>
                )}

                {/* Save draft modal */}
                {saveDraftOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/75 p-4">
                        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-xl">
                            <div className="mb-4 flex items-start justify-between gap-4">
                                <div>
                                    <div className="text-xl font-bold text-slate-900">Save Marketing List</div>
                                    <div className="mt-1 text-sm text-slate-600">Store the current filters as a saved list.</div>
                                </div>
                                <button type="button" className={iconBtn} onClick={() => setSaveDraftOpen(false)} aria-label="Close">
                                    ✕
                                </button>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className={labelClass}>Name</label>
                                    <input
                                        className={inputClass}
                                        value={saveDraft.name}
                                        onChange={(e) => setSaveDraft((s) => ({ ...s, name: e.target.value }))}
                                        placeholder="List name"
                                    />
                                </div>

                                <div>
                                    <label className={labelClass}>Description</label>
                                    <textarea
                                        className="w-full rounded-xl border border-slate-300 bg-white p-3 text-sm text-slate-700 shadow-sm outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-900/20"
                                        value={saveDraft.description}
                                        onChange={(e) => setSaveDraft((s) => ({ ...s, description: e.target.value }))}
                                        placeholder="Optional"
                                        rows={3}
                                    />
                                </div>
                            </div>

                            <div className="mt-6 flex justify-end gap-2">
                                <button className={btnSoft} onClick={() => setSaveDraftOpen(false)} type="button">
                                    Cancel
                                </button>
                                <button
                                    className={btnPrimary}
                                    onClick={() => {
                                        (async () => {
                                            const saveList = async () => {
                                                if (!saveDraft.name.trim()) {
                                                    toast.warn("Name required");
                                                    return;
                                                }
                                                try {
                                                    const p = buildPayload();
                                                    const { filters } = p;
                                                    const { data } = await axiosInstance.post("/marketing-lists", {
                                                        name: saveDraft.name.trim(),
                                                        description: saveDraft.description || "",
                                                        logic: logic.value,
                                                        filters,
                                                    });
                                                    toast.success("Saved");
                                                    setSaveDraftOpen(false);
                                                    setSaveDraft({ name: "", description: "" });
                                                    await loadLists();
                                                    setSelectedList(data);
                                                } catch (err) {
                                                    const msg =
                                                        err?.response?.data?.message ||
                                                        (err?.response?.status === 409 ? "Duplicate name" : "Save failed");
                                                    toast.error(msg);
                                                }
                                            };
                                            await saveList();
                                        })();
                                    }}
                                    type="button"
                                >
                                    <Save className="h-4 w-4" />
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Connect on LinkedIn modal */}
                {connectModalOpen && (
                    <div className="fixed inset-0 flex items-center justify-center bg-slate-900/75 p-4" >
                        <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-xl">
                            <div className="mb-4 flex items-start justify-between gap-4">
                                <div>
                                    <div className="flex items-center gap-2 text-xl font-bold text-slate-900">
                                        <Linkedin className="h-5 w-5" />
                                        LinkedIn Connect
                                    </div>
                                    <div className="mt-1 text-sm text-slate-600">Run once or set a scheduler for this filter.</div>
                                </div>
                                <button type="button" className={iconBtn} onClick={() => setConnectModalOpen(false)} aria-label="Close">
                                    ✕
                                </button>
                            </div>

                            <div className="mb-4 grid grid-cols-2 gap-2">
                                <button
                                    type="button"
                                    onClick={() => setConnectMode("once")}
                                    className={`h-11 rounded-2xl border text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-slate-900/20 ${connectMode === "once"
                                        ? "border-slate-900 bg-slate-900 text-white"
                                        : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
                                        }`}
                                >
                                    Once-off
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setConnectMode("schedule")}
                                    className={`h-11 rounded-2xl border text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-slate-900/20 ${connectMode === "schedule"
                                        ? "border-slate-900 bg-slate-900 text-white"
                                        : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
                                        }`}
                                >
                                    Scheduler
                                </button>
                            </div>

                            {connectMode === "once" ? (
                                <div className="flex justify-end gap-2">
                                    <button type="button" className={btnSoft} onClick={() => setConnectModalOpen(false)}>
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className={btnPrimary}
                                        disabled={loading || expandiBusy}
                                        onClick={async () => {
                                            await handleExportToExpandi();
                                            setConnectModalOpen(false);
                                        }}
                                    >
                                        <Linkedin className="h-4 w-4" />
                                        Run once
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="space-y-4">
                                        {linkedinSchedulerInfo ? (
                                            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-900">
                                                <div className="font-bold">Existing Scheduler</div>
                                                <ul className="mt-2 list-inside list-disc space-y-1 text-slate-800">
                                                    <li>
                                                        <span className="font-semibold">Frequency:</span> Send to connection queue on a <span className="font-bold">{linkedinSchedulerInfo.frequency}</span> basis
                                                    </li>
                                                    <li>
                                                        <span className="font-semibold">Skip sent:</span>{" "}
                                                        {linkedinSchedulerInfo.skip_already_sent ? "Yes" : "No"}
                                                    </li>
                                                    <li>
                                                        <span className="font-semibold">Last run:</span>{" "}
                                                        {linkedinSchedulerInfo.last_run_at ? fmtDate(linkedinSchedulerInfo.last_run_at) : "N/A"}
                                                    </li>
                                                </ul>

                                                <button
                                                    type="button"
                                                    className="mt-3 inline-flex h-10 items-center rounded-2xl bg-rose-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500/40 disabled:opacity-50"
                                                    disabled={connectSchedulingBusy}
                                                    onClick={handleDeleteScheduler(linkedinSchedulerInfo.marketing_list_scheduler_id)}
                                                >
                                                    Delete existing scheduler
                                                </button>
                                            </div>
                                        ) : null}

                                        <div>
                                            <label className={labelClass}>Frequency</label>
                                            <Select
                                                options={scheduleFreqOptions}
                                                value={connectFreq}
                                                onChange={(v) => setConnectFreq(v || scheduleFreqOptions[0])}
                                                classNamePrefix="react-select"
                                                styles={selectStyles44}
                                            />
                                        </div>

                                        <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm hover:bg-slate-50">
                                            <input
                                                type="checkbox"
                                                checked={!!connectSkipSent}
                                                onChange={(e) => setConnectSkipSent(e.target.checked)}
                                                className="h-4 w-4 accent-slate-900"
                                            />
                                            <span className="text-sm font-semibold text-slate-900">Skip already sent/connected</span>
                                        </label>
                                    </div>

                                    <div className="mt-6 flex justify-end gap-2">
                                        <button
                                            type="button"
                                            className={btnSoft}
                                            onClick={() => setConnectModalOpen(false)}
                                            disabled={connectSchedulingBusy}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className={btnPrimary}
                                            disabled={connectSchedulingBusy}
                                            onClick={handleScheduleLinkedInConnect}
                                        >
                                            {connectSchedulingBusy ? (
                                                <span className="flex items-center gap-2">
                                                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                                                    Setting...
                                                </span>
                                            ) : (
                                                <>
                                                    <ChevronDown className="h-4 w-4" />
                                                    Set/Update scheduler
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* Email queue modal */}
                {emailQueueModalOpen && (
                    <div className="fixed inset-0 flex items-center justify-center bg-slate-900/75 p-4">
                        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-xl">
                            <div className="mb-4 flex items-start justify-between gap-4">
                                <div>
                                    <div className="flex items-center gap-2 text-xl font-bold text-slate-900">
                                        <Mail className="h-5 w-5" />
                                        Email Queue
                                    </div>
                                    <div className="mt-1 text-sm text-slate-600">Run once or set a scheduler for this filter.</div>
                                </div>
                                <button type="button" className={iconBtn} onClick={() => setEmailQueueModalOpen(false)} aria-label="Close">
                                    ✕
                                </button>
                            </div>

                            <div className="mb-4 grid grid-cols-2 gap-2">
                                <button
                                    type="button"
                                    onClick={() => setEmailMode("once")}
                                    className={`h-11 rounded-2xl border text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-slate-900/20 ${emailMode === "once"
                                        ? "border-slate-900 bg-slate-900 text-white"
                                        : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
                                        }`}
                                >
                                    Once-off
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setEmailMode("schedule")}
                                    className={`h-11 rounded-2xl border text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-slate-900/20 ${emailMode === "schedule"
                                        ? "border-slate-900 bg-slate-900 text-white"
                                        : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
                                        }`}
                                >
                                    Scheduler
                                </button>
                            </div>

                            {emailMode === "once" ? (
                                <div className="flex justify-end gap-2">
                                    <button type="button" className={btnSoft} onClick={() => setEmailQueueModalOpen(false)}>
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className={btnPrimary}
                                        disabled={loading}
                                        onClick={async () => {
                                            await handleAddtoEmailQueue();
                                            setEmailQueueModalOpen(false);
                                        }}
                                    >
                                        <Mail className="h-4 w-4" />
                                        Run once
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="space-y-4">
                                        {emailSchedulerInfo ? (
                                            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-900">
                                                <div className="font-bold">Existing scheduler</div>
                                                <ul className="mt-2 list-inside list-disc space-y-1 text-slate-800">
                                                    <li>
                                                        <span className="font-semibold">Frequency:</span> Send to connection queue on a <span className="font-bold">{emailSchedulerInfo.frequency}</span> basis
                                                    </li>
                                                    <li>
                                                        <span className="font-semibold">Skip already sent:</span>{" "}
                                                        {emailSchedulerInfo.skip_already_sent ? "Yes" : "No"}
                                                    </li>
                                                    <li>
                                                        <span className="font-semibold">Last run:</span>{" "}
                                                        {emailSchedulerInfo.last_run_at ? fmtDate(emailSchedulerInfo.last_run_at) : "N/A"}
                                                    </li>
                                                </ul>

                                                <button
                                                    type="button"
                                                    className="mt-3 inline-flex h-10 items-center rounded-2xl bg-rose-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500/40 disabled:opacity-50"
                                                    disabled={connectSchedulingBusy}
                                                    onClick={handleDeleteScheduler(emailSchedulerInfo.marketing_list_scheduler_id)}
                                                >
                                                    Delete existing scheduler
                                                </button>
                                            </div>
                                        ) : null}

                                        <div>
                                            <label className={labelClass}>Frequency</label>
                                            <Select
                                                options={scheduleFreqOptions}
                                                value={emailFreq}
                                                onChange={(v) => setEmailFreq(v || scheduleFreqOptions[0])}
                                                classNamePrefix="react-select"
                                                styles={selectStyles44}
                                            />
                                        </div>

                                        <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm hover:bg-slate-50">
                                            <input
                                                type="checkbox"
                                                checked={!!emailSkipSent}
                                                onChange={(e) => setEmailSkipSent(e.target.checked)}
                                                className="h-4 w-4 accent-slate-900"
                                            />
                                            <span className="text-sm font-semibold text-slate-900">Skip already sent/queued</span>
                                        </label>
                                    </div>

                                    <div className="mt-6 flex justify-end gap-2">
                                        <button
                                            type="button"
                                            className={btnSoft}
                                            onClick={() => setEmailQueueModalOpen(false)}
                                            disabled={emailSchedulingBusy}
                                        >
                                            Cancel
                                        </button>
                                        <button type="button" className={btnPrimary} disabled={emailSchedulingBusy} onClick={handleScheduleEmailQueue}>
                                            {emailSchedulingBusy ? (
                                                <span className="flex items-center gap-2">
                                                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                                                    Setting...
                                                </span>
                                            ) : (
                                                <>
                                                    <ChevronDown className="h-4 w-4" />
                                                    Set/Update scheduler
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
